import {
  GarageDoor,
  Hangar,
  HangarBay,
  Obstacle,
  ObstacleType,
} from "../../../types";
import { round } from "../../../utils/lodash-replacements";
import { feetToMeters } from "../../../utils/math";

export const isAlmostEqual = (num1: number, num2: number) => {
  return Math.abs(num1 - num2) < 0.9;
};

export const drawDoors = (
  ctx: CanvasRenderingContext2D,
  hangar: Hangar,
  feetToPixels: (ft: number) => number
) => {
  ctx.beginPath();
  ctx.save();
  if (hangar.left_door) {
    ctx.setLineDash([]);
    ctx.lineWidth = 0;
    ctx.strokeStyle = "black";
    ctx.fillStyle = "black";
    ctx.moveTo(0, ctx.canvas.height);
    ctx.lineTo(feetToPixels(hangar.left_door), ctx.canvas.height);
    ctx.lineTo(feetToPixels(hangar.left_door), ctx.canvas.height - 20);
    ctx.lineTo(0, ctx.canvas.height - 20);
    ctx.fill();
  }
  if (hangar.right_door) {
    ctx.setLineDash([]);
    ctx.strokeStyle = "black";
    ctx.fillStyle = "black";
    ctx.moveTo(
      ctx.canvas.width - feetToPixels(hangar.right_door),
      ctx.canvas.height
    );
    ctx.lineTo(ctx.canvas.width, ctx.canvas.height);
    ctx.lineTo(ctx.canvas.width, ctx.canvas.height - 20);
    ctx.lineTo(
      ctx.canvas.width - feetToPixels(hangar.right_door),
      ctx.canvas.height - 20
    );
    ctx.fill();
  }
  ctx.restore();
};

export const drawGarageDoors = (
  ctx: CanvasRenderingContext2D,
  garageDoors: GarageDoor[],
  feetToPixels: (ft: number) => number
) => {
  ctx.beginPath();
  ctx.save();
  for (const garageDoor of garageDoors) {
    ctx.setLineDash([]);
    ctx.lineWidth = 8;
    ctx.strokeStyle = "#FFAC1C";
    ctx.fillStyle = "#FFAC1C";
    if (garageDoor.wall === "back") {
      ctx.moveTo(
        feetToPixels(garageDoor.x),
        ctx.canvas.height - feetToPixels(garageDoor.y)
      );
      ctx.lineTo(
        feetToPixels(garageDoor.x + garageDoor.width),
        ctx.canvas.height - feetToPixels(garageDoor.y)
      );
    } else {
      ctx.moveTo(
        feetToPixels(garageDoor.x),
        ctx.canvas.height - feetToPixels(garageDoor.y)
      );
      ctx.lineTo(
        feetToPixels(garageDoor.x),
        ctx.canvas.height - feetToPixels(garageDoor.y + garageDoor.width)
      );
    }
    ctx.stroke();
    ctx.fill();
  }
  ctx.restore();
};

export const drawUnobstructiveObstacles = (
  ctx: CanvasRenderingContext2D,
  hangar: Hangar,
  _feetToPixels: (ft: number) => number,
  _options?: {
    lineWidth?: number;
    lineDash?: number[];
  }
) => {
  ctx.beginPath();
  ctx.save();

  for (const _obstacle of hangar.obstacles.filter(
    (o) => o.type === ObstacleType.UNOBSTRUCTIVE
  )) {
  }
  ctx.restore();
};

export const drawSpacingBuffers = (
  ctx: CanvasRenderingContext2D,
  hangar: Hangar,
  feetToPixels: (ft: number) => number,
  options?: {
    lineWidth?: number;
    lineDash?: number[];
    showWallSpacingFill?: boolean;
  }
) => {
  ctx.beginPath();
  ctx.save();

  const lineWidth = options?.lineWidth ?? 2;
  const lineDash = options?.lineDash ?? [4, 4];
  const showWallSpacingFill = options?.showWallSpacingFill ?? false;

  // Draw all spacing rectangles (wall spacing + obstacle extensions) in one path
  ctx.beginPath();
  ctx.fillStyle = "rgba(255, 0, 0, 0.2)"; // #FF0000 at 20% opacity

  // Left wall spacing area - full height
  if (hangar.wall_spacing_left) {
    ctx.rect(
      0, // x start
      0, // y start (full height)
      Math.round(feetToPixels(hangar.wall_spacing_left)), // width
      ctx.canvas.height // full height
    );
  }

  // Back wall spacing area - full width
  if (hangar.wall_spacing_back) {
    ctx.rect(
      0, // x start (full width)
      0, // y start
      ctx.canvas.width, // full width
      Math.round(feetToPixels(hangar.wall_spacing_back)) // height
    );
  }

  // Right wall spacing area - full height
  if (hangar.wall_spacing_right) {
    ctx.rect(
      Math.round(feetToPixels(hangar.width - hangar.wall_spacing_right)), // x start
      0, // y start (full height)
      Math.round(feetToPixels(hangar.wall_spacing_right)), // width
      ctx.canvas.height // full height
    );
  }
  // Add spacing buffer extensions around wall-touching obstacles to the same path
  if (hangar.obstacles) {
    for (const obstacle of hangar.obstacles) {
      if (
        obstacle.type === ObstacleType.UNOBSTRUCTIVE ||
        obstacle.type === ObstacleType.CAUTION
      ) {
        continue;
      }

      const touchesWall =
        obstacle.y === 0 ||
        obstacle.x === 0 ||
        isAlmostEqual(obstacle.x + obstacle.width, hangar.width);

      if (touchesWall) {
        // Draw spacing buffer extension around the obstacle
        if (obstacle.y === 0 && obstacle.x === 0) {
          // top left corner
          ctx.rect(
            Math.round(feetToPixels(0)),
            Math.round(feetToPixels(0)),
            Math.round(
              feetToPixels(obstacle.width + (hangar.wall_spacing_left || 0))
            ),
            Math.round(
              feetToPixels(obstacle.depth + (hangar.wall_spacing_back || 0))
            )
          );
        } else if (
          obstacle.y === 0 &&
          isAlmostEqual(obstacle.x + obstacle.width, hangar.width)
        ) {
          // top right corner
          ctx.rect(
            Math.round(
              feetToPixels(obstacle.x - (hangar.wall_spacing_right || 0))
            ),
            Math.round(feetToPixels(0)),
            Math.round(
              feetToPixels(
                obstacle.width + (hangar.wall_spacing_right || 0) * 2
              )
            ),
            Math.round(
              feetToPixels(obstacle.depth + (hangar.wall_spacing_back || 0))
            )
          );
        } else if (obstacle.y === 0) {
          // back wall
          ctx.rect(
            Math.round(
              feetToPixels(obstacle.x - (hangar.wall_spacing_back || 0))
            ),
            Math.round(feetToPixels(0)),
            Math.round(
              feetToPixels(obstacle.width + (hangar.wall_spacing_back || 0) * 2)
            ),
            Math.round(
              feetToPixels(obstacle.depth + (hangar.wall_spacing_back || 0))
            )
          );
        } else if (obstacle.x === 0) {
          // left wall
          ctx.rect(
            Math.round(feetToPixels(0)),
            Math.round(
              feetToPixels(obstacle.y - (hangar.wall_spacing_left || 0))
            ),
            Math.round(
              feetToPixels(obstacle.width + (hangar.wall_spacing_left || 0))
            ),
            Math.round(
              feetToPixels(obstacle.depth + (hangar.wall_spacing_left || 0) * 2)
            )
          );
        } else if (isAlmostEqual(obstacle.x + obstacle.width, hangar.width)) {
          // right wall
          ctx.rect(
            Math.round(
              feetToPixels(
                hangar.width - obstacle.width - (hangar.wall_spacing_right || 0)
              )
            ),
            Math.round(
              feetToPixels(obstacle.y - (hangar.wall_spacing_right || 0))
            ),
            Math.round(
              feetToPixels(
                obstacle.width + (hangar.wall_spacing_right || 0) * 2
              )
            ),
            Math.round(
              feetToPixels(
                obstacle.depth + (hangar.wall_spacing_right || 0) * 2
              )
            )
          );
        }
      }
    }
  }

  // Fill all rectangles only if showWallSpacingFill is enabled
  if (showWallSpacingFill) {
    ctx.fill();
  }

  // Only draw dotted boundaries when fill is disabled
  if (!showWallSpacingFill) {
    // Draw dashed boundary lines at the edges of spacing areas
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = "red";
    ctx.setLineDash(lineDash);
    ctx.beginPath();

    // Calculate boundaries to prevent corner intersections
    // Round to whole pixels to ensure crisp lines
    const leftBoundaryX = hangar.wall_spacing_left
      ? Math.round(feetToPixels(hangar.wall_spacing_left))
      : 0;
    const rightBoundaryX = hangar.wall_spacing_right
      ? Math.round(feetToPixels(hangar.width - hangar.wall_spacing_right))
      : ctx.canvas.width;
    const backBoundaryY = hangar.wall_spacing_back
      ? Math.round(feetToPixels(hangar.wall_spacing_back))
      : 0;

    // Left wall spacing boundary - stop at back spacing if present
    if (hangar.wall_spacing_left) {
      ctx.moveTo(leftBoundaryX, backBoundaryY);
      ctx.lineTo(leftBoundaryX, ctx.canvas.height);
    }

    // Back wall spacing boundary - draw between left and right spacing
    if (hangar.wall_spacing_back) {
      ctx.moveTo(leftBoundaryX, backBoundaryY);
      ctx.lineTo(rightBoundaryX, backBoundaryY);
    }

    // Right wall spacing boundary - stop at back spacing if present
    if (hangar.wall_spacing_right) {
      ctx.moveTo(rightBoundaryX, backBoundaryY);
      ctx.lineTo(rightBoundaryX, ctx.canvas.height);
    }

    // Draw obstacle extension boundaries for wall-touching obstacles
    if (hangar.obstacles) {
      for (const obstacle of hangar.obstacles) {
        if (
          obstacle.type === ObstacleType.UNOBSTRUCTIVE ||
          obstacle.type === ObstacleType.CAUTION
        ) {
          continue;
        }

        const touchesWall =
          obstacle.y === 0 ||
          obstacle.x === 0 ||
          isAlmostEqual(obstacle.x + obstacle.width, hangar.width);

        if (touchesWall) {
          // Draw only the necessary perimeter boundaries
          if (obstacle.y === 0 && obstacle.x === 0) {
            // top left corner - only draw the extensions beyond wall spacing
            const rightEdge = Math.round(
              feetToPixels(obstacle.width + (hangar.wall_spacing_left || 0))
            );
            const bottomEdge = Math.round(
              feetToPixels(obstacle.depth + (hangar.wall_spacing_back || 0))
            );
            ctx.moveTo(rightEdge, 0);
            ctx.lineTo(rightEdge, bottomEdge);
            ctx.moveTo(0, bottomEdge);
            ctx.lineTo(rightEdge, bottomEdge);
          } else if (
            obstacle.y === 0 &&
            isAlmostEqual(obstacle.x + obstacle.width, hangar.width)
          ) {
            // top right corner - only draw the extensions beyond wall spacing
            const leftEdge = Math.round(
              feetToPixels(obstacle.x - (hangar.wall_spacing_right || 0))
            );
            const bottomEdge = Math.round(
              feetToPixels(obstacle.depth + (hangar.wall_spacing_back || 0))
            );
            ctx.moveTo(leftEdge, 0);
            ctx.lineTo(leftEdge, bottomEdge);
            ctx.moveTo(leftEdge, bottomEdge);
            ctx.lineTo(ctx.canvas.width, bottomEdge);
          } else if (obstacle.y === 0) {
            // back wall - only draw horizontal line that outlines the filled area, skip vertical lines that are internal
            const leftEdge = Math.round(
              feetToPixels(obstacle.x - (hangar.wall_spacing_back || 0))
            );
            const rightEdge = Math.round(
              feetToPixels(
                obstacle.x + obstacle.width + (hangar.wall_spacing_back || 0)
              )
            );
            const bottomEdge = Math.round(
              feetToPixels(obstacle.depth + (hangar.wall_spacing_back || 0))
            );

            // Only draw horizontal line that outlines the filled area (don't draw vertical lines when fill is enabled)
            ctx.moveTo(
              Math.max(
                leftEdge,
                Math.round(feetToPixels(hangar.wall_spacing_left || 0))
              ),
              bottomEdge
            );
            ctx.lineTo(
              Math.min(
                rightEdge,
                Math.round(
                  feetToPixels(hangar.width - (hangar.wall_spacing_right || 0))
                )
              ),
              bottomEdge
            );
          } else if (obstacle.x === 0) {
            // left wall - only draw the right vertical boundary that outlines the filled area
            const topEdge = Math.round(
              feetToPixels(obstacle.y - (hangar.wall_spacing_left || 0))
            );
            const bottomEdge = Math.round(
              feetToPixels(
                obstacle.y + obstacle.depth + (hangar.wall_spacing_left || 0)
              )
            );
            const rightEdge = Math.round(
              feetToPixels(obstacle.width + (hangar.wall_spacing_left || 0))
            );

            // Only draw the right vertical line that forms the outline of the filled area
            const leftSpacingRight = Math.round(
              feetToPixels(hangar.wall_spacing_left || 0)
            );
            const backSpacingBottom = Math.round(
              feetToPixels(hangar.wall_spacing_back || 0)
            );

            // Draw right vertical line only for the part that extends beyond filled areas
            if (rightEdge > leftSpacingRight) {
              ctx.moveTo(rightEdge, Math.max(topEdge, backSpacingBottom));
              ctx.lineTo(rightEdge, bottomEdge);
            }
          } else if (isAlmostEqual(obstacle.x + obstacle.width, hangar.width)) {
            // right wall - draw outline boundaries of spacing extensions
            const topEdge = Math.round(
              feetToPixels(obstacle.y - (hangar.wall_spacing_right || 0))
            );
            const bottomEdge = Math.round(
              feetToPixels(
                obstacle.y + obstacle.depth + (hangar.wall_spacing_right || 0)
              )
            );
            const leftEdge = Math.round(
              feetToPixels(obstacle.x - (hangar.wall_spacing_right || 0))
            );

            // Draw the left vertical boundary that outlines the spacing extension
            ctx.moveTo(
              leftEdge,
              Math.max(
                topEdge,
                Math.round(feetToPixels(hangar.wall_spacing_back || 0))
              )
            );
            ctx.lineTo(leftEdge, bottomEdge);
          }
        }
      }
    }

    // Draw all boundary lines at once
    ctx.stroke();
  }
  ctx.restore();
  ctx.save();
  ctx.beginPath();
  if (hangar.obstacles) {
    for (const obstacle of hangar.obstacles) {
      if (obstacle.type === ObstacleType.UNOBSTRUCTIVE) {
        continue;
      }
      if (obstacle.type === ObstacleType.CAUTION) {
        continue;
      }
      if (obstacle.type === ObstacleType.NO_TOW_AREA) {
        // NO_TOW_AREA obstacles should use the same spacing buffer logic as regular obstructions
        // but clear a larger area when they touch walls to include the wall spacing
        const touchesWall =
          obstacle.y === 0 ||
          obstacle.x === 0 ||
          isAlmostEqual(obstacle.x + obstacle.width, hangar.width);

        if (touchesWall && showWallSpacingFill) {
          // For wall-touching NO_TOW_AREA with fill enabled, don't clear anything - let the entire area remain filled with red
          continue; // Skip to next obstacle when fill is enabled and wall is touched
        } else if (!touchesWall) {
          // For non-wall-touching NO_TOW_AREA, just clear the obstacle area and skip the boundary drawing
          ctx.clearRect(
            Math.round(feetToPixels(obstacle.x)),
            Math.round(feetToPixels(obstacle.y)),
            Math.round(feetToPixels(obstacle.width)),
            Math.round(feetToPixels(obstacle.depth))
          );
          continue; // Skip to next obstacle for non-wall-touching NO_TOW_AREA
        }
        // For wall-touching NO_TOW_AREA with fill disabled, fall through to the same boundary drawing logic as regular obstructions
      }

      // When wall spacing fill is enabled, only clear the obstacle itself (not the spacing extension)
      // to leave the red spacing buffer around the obstacle
      if (showWallSpacingFill) {
        const touchesWall =
          obstacle.y === 0 ||
          obstacle.x === 0 ||
          isAlmostEqual(obstacle.x + obstacle.width, hangar.width);
        if (touchesWall) {
          // Only clear the obstacle itself, leaving the spacing buffer filled
          ctx.clearRect(
            Math.round(feetToPixels(obstacle.x)),
            Math.round(feetToPixels(obstacle.y)),
            Math.round(feetToPixels(obstacle.width)),
            Math.round(feetToPixels(obstacle.depth))
          );
          continue;
        }
      }

      // Draw boundary lines for wall-touching obstacles when fill is disabled
      if (!showWallSpacingFill) {
        // top left corner
        if (obstacle.y === 0 && obstacle.x === 0) {
          ctx.clearRect(
            Math.round(feetToPixels(0)),
            Math.round(feetToPixels(0)),
            Math.round(feetToPixels(obstacle.width + hangar.wall_spacing_left)),
            Math.round(feetToPixels(obstacle.depth + hangar.wall_spacing_back))
          );
          ctx.setLineDash(lineDash);
          ctx.lineWidth = lineWidth;
          ctx.strokeStyle = "red";
          ctx.moveTo(
            Math.round(feetToPixels(hangar.wall_spacing_left)),
            Math.round(feetToPixels(obstacle.depth + hangar.wall_spacing_back))
          );
          ctx.lineTo(
            Math.round(feetToPixels(obstacle.width + hangar.wall_spacing_left)),
            Math.round(feetToPixels(obstacle.depth + hangar.wall_spacing_back))
          );
          ctx.lineTo(
            Math.round(feetToPixels(obstacle.width + hangar.wall_spacing_left)),
            Math.round(feetToPixels(hangar.wall_spacing_back))
          );
          ctx.stroke();
          ctx.restore();
          continue;
        }
        // top right corner
        if (
          obstacle.y === 0 &&
          isAlmostEqual(obstacle.x + obstacle.width, hangar.width)
        ) {
          ctx.clearRect(
            Math.round(feetToPixels(obstacle.x - hangar.wall_spacing_right)),
            Math.round(feetToPixels(0)),
            Math.round(
              feetToPixels(obstacle.width + hangar.wall_spacing_right * 2)
            ),
            Math.round(feetToPixels(obstacle.depth + hangar.wall_spacing_back))
          );
          ctx.setLineDash(lineDash);
          ctx.lineWidth = lineWidth;
          ctx.strokeStyle = "red";
          ctx.moveTo(
            Math.round(feetToPixels(obstacle.x - hangar.wall_spacing_right)),
            Math.round(feetToPixels(hangar.wall_spacing_back))
          );
          ctx.lineTo(
            Math.round(feetToPixels(obstacle.x - hangar.wall_spacing_right)),
            Math.round(feetToPixels(obstacle.depth + hangar.wall_spacing_back))
          );
          ctx.lineTo(
            Math.round(
              feetToPixels(
                obstacle.x + obstacle.width - hangar.wall_spacing_right
              )
            ),
            Math.round(feetToPixels(obstacle.depth + hangar.wall_spacing_back))
          );
          ctx.stroke();
          ctx.restore();
          continue;
        }
        // check to see if it is touching a wall
        if (obstacle.y === 0) {
          ctx.clearRect(
            Math.round(feetToPixels(obstacle.x - hangar.wall_spacing_back)),
            Math.round(feetToPixels(0)),
            Math.round(
              feetToPixels(obstacle.width + hangar.wall_spacing_back * 2)
            ),
            Math.round(feetToPixels(obstacle.depth + hangar.wall_spacing_back))
          );
          ctx.setLineDash(lineDash);
          ctx.lineWidth = lineWidth;
          ctx.strokeStyle = "red";
          // if the left buffer on the obstacle is within the wall spacing
          // then we aren't going to paint the left buffer
          // top left
          if (
            obstacle.x - hangar.wall_spacing_back <
            hangar.wall_spacing_back
          ) {
            ctx.moveTo(
              Math.round(feetToPixels(hangar.wall_spacing_back)),
              Math.round(
                feetToPixels(obstacle.depth + hangar.wall_spacing_back)
              )
            );
          } else {
            ctx.moveTo(
              Math.round(feetToPixels(obstacle.x - hangar.wall_spacing_back)),
              Math.round(feetToPixels(hangar.wall_spacing_back))
            );
          }

          // bottom left
          ctx.lineTo(
            Math.round(
              feetToPixels(
                Math.max(
                  hangar.wall_spacing_back,
                  obstacle.x - hangar.wall_spacing_back
                )
              )
            ),
            Math.round(feetToPixels(obstacle.depth + hangar.wall_spacing_back))
          );
          // bottom right
          ctx.lineTo(
            Math.round(
              feetToPixels(
                Math.min(
                  hangar.width - hangar.wall_spacing_back,
                  obstacle.x + obstacle.width + hangar.wall_spacing_back
                )
              )
            ),
            Math.round(feetToPixels(obstacle.depth + hangar.wall_spacing_back))
          );
          // top right
          if (
            hangar.width -
              (obstacle.x + obstacle.width + hangar.wall_spacing_back) <
            hangar.wall_spacing_back
          ) {
            // do nothing
          } else {
            ctx.lineTo(
              Math.round(
                feetToPixels(
                  obstacle.x + obstacle.width + hangar.wall_spacing_back
                )
              ),
              Math.round(feetToPixels(hangar.wall_spacing_back))
            );
          }
          ctx.stroke();
        }
        if (obstacle.x === 0) {
          ctx.clearRect(
            Math.round(feetToPixels(0)),
            Math.round(feetToPixels(obstacle.y - hangar.wall_spacing_left)),
            Math.round(feetToPixels(obstacle.width + hangar.wall_spacing_left)),
            Math.round(
              feetToPixels(obstacle.depth + hangar.wall_spacing_left * 2)
            )
          );
          ctx.setLineDash(lineDash);
          ctx.lineWidth = lineWidth;
          ctx.strokeStyle = "red";
          // if the top buffer on the obstacle is within the wall spacing
          // then we aren't going to paint the top buffer
          if (
            obstacle.y - hangar.wall_spacing_left <
            hangar.wall_spacing_left
          ) {
            ctx.moveTo(
              Math.round(
                feetToPixels(
                  obstacle.x + obstacle.width + hangar.wall_spacing_left
                )
              ),
              Math.round(feetToPixels(hangar.wall_spacing_left))
            );
          } else {
            ctx.moveTo(
              Math.round(feetToPixels(obstacle.x + hangar.wall_spacing_left)),
              Math.round(feetToPixels(obstacle.y - hangar.wall_spacing_left))
            );
          }

          ctx.lineTo(
            Math.round(
              feetToPixels(
                obstacle.x + obstacle.width + hangar.wall_spacing_left
              )
            ),
            Math.round(
              feetToPixels(
                Math.max(
                  hangar.wall_spacing_left,
                  obstacle.y - hangar.wall_spacing_left
                )
              )
            )
          );
          ctx.lineTo(
            Math.round(
              feetToPixels(
                obstacle.x + obstacle.width + hangar.wall_spacing_left
              )
            ),
            Math.round(
              feetToPixels(
                obstacle.y + obstacle.depth + hangar.wall_spacing_left
              )
            )
          );
          ctx.lineTo(
            Math.round(feetToPixels(obstacle.x + hangar.wall_spacing_left)),
            Math.round(
              feetToPixels(
                obstacle.y + obstacle.depth + hangar.wall_spacing_left
              )
            )
          );
          ctx.stroke();
        }
        if (isAlmostEqual(obstacle.x + obstacle.width, hangar.width)) {
          ctx.clearRect(
            Math.round(
              feetToPixels(
                hangar.width - obstacle.width - hangar.wall_spacing_right
              )
            ),
            Math.round(feetToPixels(obstacle.y - hangar.wall_spacing_right)),
            Math.round(
              feetToPixels(obstacle.width + hangar.wall_spacing_right * 2)
            ) + lineWidth,
            Math.round(
              feetToPixels(obstacle.depth + hangar.wall_spacing_right * 2)
            ) + lineWidth
          );
          ctx.setLineDash(lineDash);
          ctx.lineWidth = lineWidth;
          ctx.strokeStyle = "red";
          if (
            obstacle.y - hangar.wall_spacing_right <
            hangar.wall_spacing_right
          ) {
            ctx.moveTo(
              Math.round(
                feetToPixels(
                  hangar.width - hangar.wall_spacing_right - obstacle.width
                )
              ),
              Math.round(feetToPixels(hangar.wall_spacing_right))
            );
          } else {
            ctx.moveTo(
              Math.round(
                feetToPixels(hangar.width - hangar.wall_spacing_right)
              ),
              Math.round(feetToPixels(obstacle.y - hangar.wall_spacing_right))
            );
          }
          ctx.lineTo(
            Math.round(
              feetToPixels(
                hangar.width - hangar.wall_spacing_right - obstacle.width
              )
            ),
            Math.round(
              feetToPixels(
                Math.max(
                  hangar.wall_spacing_right,
                  obstacle.y - hangar.wall_spacing_right
                )
              )
            )
          );
          ctx.lineTo(
            Math.round(
              feetToPixels(
                hangar.width - hangar.wall_spacing_right - obstacle.width
              )
            ),
            Math.round(
              feetToPixels(
                obstacle.y + obstacle.depth + hangar.wall_spacing_right
              )
            )
          );
          ctx.lineTo(
            Math.round(feetToPixels(hangar.width - hangar.wall_spacing_right)),
            Math.round(
              feetToPixels(
                obstacle.y + obstacle.depth + hangar.wall_spacing_right
              )
            )
          );
          ctx.stroke();
        }
      } // End of showWallSpacingFill conditional
    }
  }
  ctx.restore();
};

export const drawDistanceMarkers = (
  ctx: CanvasRenderingContext2D,
  hangar: Hangar,
  obstacle: Obstacle,
  feetToPixels: (ft: number) => number,
  unit: string = "ft"
) => {
  if (!obstacle) {
    return;
  }
  const obstacleInPixels: Obstacle = {
    ...obstacle,
    x: feetToPixels(obstacle.x),
    y: feetToPixels(obstacle.y),
    width: feetToPixels(obstacle.width),
    depth: feetToPixels(obstacle.depth),
    height: feetToPixels(obstacle.height),
  };
  ctx.beginPath();
  ctx.save();

  ctx.beginPath();
  ctx.lineWidth = 4;
  ctx.setLineDash([4]);
  ctx.strokeStyle = "skyblue";
  // horizontal
  ctx.moveTo(0, obstacleInPixels.y + obstacleInPixels.depth / 2);
  ctx.lineTo(ctx.canvas.width, obstacleInPixels.y + obstacleInPixels.depth / 2);
  ctx.stroke();
  // vertical
  ctx.moveTo(obstacleInPixels.x + obstacleInPixels.width / 2, 0);
  ctx.lineTo(
    obstacleInPixels.x + obstacleInPixels.width / 2,
    ctx.canvas.height
  );
  ctx.stroke();

  ctx.textAlign = "center";
  ctx.font = "14px Lexend Deca";
  ctx.fillStyle = "black";
  // left
  labelMeasuringLine(
    ctx,
    obstacleInPixels,
    obstacleInPixels.x / 2,
    obstacleInPixels.y + obstacleInPixels.depth / 2 + 14,
    obstacle.x,
    unit
  );
  // top
  labelMeasuringLine(
    ctx,
    obstacleInPixels,
    (obstacleInPixels.x + (obstacleInPixels.x + obstacleInPixels.width)) / 2,
    obstacleInPixels.y / 2,
    obstacle.y,
    unit
  );
  // bottom
  labelMeasuringLine(
    ctx,
    obstacleInPixels,
    (obstacleInPixels.x + (obstacleInPixels.x + obstacleInPixels.width)) / 2,
    ctx.canvas.height -
      (ctx.canvas.height - obstacleInPixels.y - obstacleInPixels.depth) / 2,
    hangar.depth - obstacle.y - obstacle.depth,
    unit
  );
  // right
  labelMeasuringLine(
    ctx,
    obstacleInPixels,
    ctx.canvas.width -
      (ctx.canvas.width - obstacleInPixels.x - obstacleInPixels.width) / 2,
    obstacleInPixels.y + obstacleInPixels.depth / 2 + 14,
    hangar.width - obstacle.x - obstacle.width,
    unit
  );
  ctx.restore();
};

export const labelMeasuringLine = (
  ctx: CanvasRenderingContext2D,
  obstacleInPixels: Obstacle,
  x: number,
  y: number,
  size: number,
  unit: string = "ft"
) => {
  size = unit === "m" ? feetToMeters(size) : size;
  // if smushed to the back
  if (y < 20) {
    const value = Math.abs(round(size, 2));
    ctx.fillText(
      `${value} ${unit}`,
      x - 30 - obstacleInPixels.width / 2,
      y + 10
    );
    return;
  }
  // if smushed to the left
  if (x < 20) {
    const value = Math.abs(round(size, 2));
    ctx.fillText(`${value} ${unit}`, 30, y + obstacleInPixels.depth / 2);
    return;
  }
  // if smushed to the right
  if (x > ctx.canvas.width - 20) {
    const value = Math.abs(round(size, 2));
    ctx.fillText(
      `${value} ${unit}`,
      ctx.canvas.width - 30,
      y + obstacleInPixels.depth / 2
    );
    return;
  }
  // if smushed to the bottom
  if (y > ctx.canvas.height - 20) {
    const value = Math.abs(round(size, 2));
    ctx.fillText(
      `${value} ${unit}`,
      x - 30 - obstacleInPixels.width / 2,
      y - 10
    );
    return;
  }
  ctx.fillText(`${Math.abs(round(size, 2))} ${unit}`, x, y);
};

export const drawGarageDoorMarkers = (
  ctx: CanvasRenderingContext2D,
  hangar: Hangar,
  garageDoors: GarageDoor[],
  feetToPixels: (ft: number) => number,
  unit: string = "ft"
) => {
  if (!garageDoors) {
    return;
  }

  for (const garageDoor of garageDoors) {
    if (!garageDoor.active) {
      continue;
    }
    ctx.lineWidth = 2;
    ctx.setLineDash([8]);
    ctx.strokeStyle = "skyblue";
    ctx.textAlign = "center";
    ctx.font = "14px Lexend Deca";
    ctx.fillStyle = "black";
    if (garageDoor.wall === "back") {
      const x = feetToPixels(garageDoor.x);
      const y = feetToPixels(2);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(ctx.canvas.width, y);
      ctx.stroke();
      const value1 =
        unit === "m"
          ? Math.abs(round(feetToMeters(garageDoor.x), 2))
          : Math.abs(round(garageDoor.x, 2));
      ctx.fillText(`${value1} ${unit}`, Math.max(40, x / 2), y * 3);
      const value2 =
        unit === "m"
          ? Math.abs(
              round(
                feetToMeters(hangar.width - garageDoor.width - garageDoor.x),
                2
              )
            )
          : Math.abs(round(hangar.width - garageDoor.width - garageDoor.x, 2));
      ctx.fillText(
        `${value2} ${unit}`,
        Math.min(
          ctx.canvas.width - 40,
          feetToPixels(
            garageDoor.x +
              garageDoor.width +
              (hangar.width - garageDoor.x - garageDoor.width) / 2
          )
        ),
        y * 3
      );
      ctx.restore();
    } else if (garageDoor.wall === "left") {
      const x = feetToPixels(2);
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, ctx.canvas.height);
      ctx.stroke();
      const value1 =
        unit === "m"
          ? Math.abs(round(feetToMeters(garageDoor.y), 2))
          : Math.abs(round(garageDoor.y, 2));
      ctx.fillText(
        `${value1} ${unit}`,
        x * 5,
        feetToPixels(Math.max(10, garageDoor.y)) / 2
      );
      const value2 =
        unit === "m"
          ? Math.abs(
              round(
                feetToMeters(hangar.depth - garageDoor.y - garageDoor.width),
                2
              )
            )
          : Math.abs(round(hangar.depth - garageDoor.y - garageDoor.width, 2));
      ctx.fillText(
        `${value2} ${unit}`,
        x * 5,
        feetToPixels(
          Math.min(
            hangar.depth - 10,
            (hangar.depth + garageDoor.y + garageDoor.width) / 2
          )
        )
      );
      ctx.restore();
    } else if (garageDoor.wall === "right") {
      const x = feetToPixels(hangar.width - 15);
      ctx.beginPath();
      ctx.moveTo(feetToPixels(hangar.width - 1), 0);
      ctx.lineTo(feetToPixels(hangar.width - 1), ctx.canvas.height);
      ctx.stroke();
      const value1 =
        unit === "m"
          ? Math.abs(round(feetToMeters(garageDoor.y), 2))
          : Math.abs(round(garageDoor.y, 2));
      ctx.fillText(
        `${value1} ${unit}`,
        x,
        feetToPixels(Math.max(10, garageDoor.y)) / 2
      );
      const value2 =
        unit === "m"
          ? Math.abs(
              round(
                feetToMeters(hangar.depth - garageDoor.y - garageDoor.width),
                2
              )
            )
          : Math.abs(round(hangar.depth - garageDoor.y - garageDoor.width, 2));
      ctx.fillText(
        `${value2} ${unit}`,
        x,
        feetToPixels(
          Math.min(
            hangar.depth - 10,
            (hangar.depth + garageDoor.y + garageDoor.width) / 2
          )
        )
      );
      ctx.restore();
    }
  }
};

export const clearBackgroundForObstacles = (
  ctx: CanvasRenderingContext2D,
  hangar: Hangar,
  feetToPixels: (ft: number) => number
) => {
  // ctx.beginPath();
  for (const obstacle of hangar.obstacles) {
    if (obstacle.type === ObstacleType.UNOBSTRUCTIVE) {
      continue;
    }
    if (obstacle.type === ObstacleType.CAUTION) {
      continue;
    }
    const obstacleInPixels: Obstacle = {
      ...obstacle,
      x: feetToPixels(obstacle.x),
      y: feetToPixels(obstacle.y),
      width: feetToPixels(obstacle.width),
      depth: feetToPixels(obstacle.depth),
      height: feetToPixels(obstacle.height),
    };
    ctx.clearRect(
      obstacleInPixels.x,
      obstacleInPixels.y,
      obstacleInPixels.width,
      obstacleInPixels.depth
    );
  }
  // ctx.save();
};

/**
 * Draw hangar bay boundaries with color-coding by height
 *
 * Renders bay polygons as semi-transparent overlays with:
 * - Color gradient: Red (low) → Yellow (mid) → Green (high) based on clear_height
 * - Dashed white stroke for bay boundaries
 * - Height labels at bay centers
 *
 * @param ctx - Canvas 2D context
 * @param bays - Array of hangar bay definitions with geometry and heights
 * @param feetToPixels - Conversion function from feet to canvas pixels
 * @param options - Optional display settings
 */
export const drawBayBoundaries = (
  ctx: CanvasRenderingContext2D,
  bays: HangarBay[],
  hangar: { width: number; depth: number },
  feetToPixels: (ft: number) => number,
  options?: {
    showLabels?: boolean;
    showHeights?: boolean;
  }
) => {
  if (!bays || bays.length === 0) return;
  if (bays.length === 1) {
    // Single bay - no dividers needed, just draw labels
    drawBayLabel(ctx, bays[0], 0, hangar, feetToPixels, false, options);
    return;
  }

  ctx.save();

  // Detect orientation: longitudinal if all bays share X-range (full width)
  const isLongitudinal = detectLongitudinalOrientation(bays);

  // Draw divider lines only (not exterior edges)
  if (isLongitudinal) {
    drawLongitudinalDividers(ctx, bays, hangar, feetToPixels);
  } else {
    drawLateralDividers(ctx, bays, feetToPixels);
  }

  // Draw bay labels with adjusted positioning
  bays.forEach((bay, index) => {
    drawBayLabel(
      ctx,
      bay,
      index,
      hangar,
      feetToPixels,
      isLongitudinal,
      options
    );
  });

  ctx.restore();
};

/**
 * Draw uncovered areas with hatching pattern to highlight gaps in bay coverage
 * Makes it visually obvious which parts of the hangar lack bay definitions
 */
export const drawUncoveredAreas = (
  ctx: CanvasRenderingContext2D,
  bays: HangarBay[],
  hangar: { width: number; depth: number },
  feetToPixels: (ft: number) => number
) => {
  if (!bays || bays.length === 0) return;

  ctx.save();

  const isLongitudinal = detectLongitudinalOrientation(bays);

  if (isLongitudinal) {
    // Calculate maximum depth covered by bays
    const coveredDepth = bays.reduce((max, bay) => {
      const coords = bay.geom.coordinates[0];
      const yValues = coords.map((c) => c[1]);
      return Math.max(max, Math.max(...yValues));
    }, 0);

    if (coveredDepth < hangar.depth - 0.1) {
      // Draw uncovered area at back
      const uncoveredStartPx = feetToPixels(coveredDepth);
      const uncoveredHeightPx = feetToPixels(hangar.depth - coveredDepth);

      // Fill with semi-transparent red
      ctx.fillStyle = "rgba(255, 0, 0, 0.08)";
      ctx.fillRect(
        0,
        uncoveredStartPx,
        feetToPixels(hangar.width),
        uncoveredHeightPx
      );

      // Add diagonal hatching
      ctx.strokeStyle = "rgba(255, 0, 0, 0.3)";
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);

      for (let x = 0; x < feetToPixels(hangar.width); x += 20) {
        ctx.beginPath();
        ctx.moveTo(x, uncoveredStartPx);
        ctx.lineTo(x + uncoveredHeightPx, uncoveredStartPx + uncoveredHeightPx);
        ctx.stroke();
      }

      // Label uncovered area
      ctx.setLineDash([]);
      ctx.fillStyle = "#c62828";
      ctx.font = "bold 14px Lexend Deca, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(
        `UNCOVERED: ${(hangar.depth - coveredDepth).toFixed(1)}ft`,
        feetToPixels(hangar.width / 2),
        uncoveredStartPx + uncoveredHeightPx / 2
      );
    }
  } else {
    // Lateral bays - calculate maximum width covered
    const coveredWidth = bays.reduce((max, bay) => {
      const coords = bay.geom.coordinates[0];
      const xValues = coords.map((c) => c[0]);
      return Math.max(max, Math.max(...xValues));
    }, 0);

    if (coveredWidth < hangar.width - 0.1) {
      // Draw uncovered area on right side
      const uncoveredStartPx = feetToPixels(coveredWidth);
      const uncoveredWidthPx = feetToPixels(hangar.width - coveredWidth);

      // Fill with semi-transparent red
      ctx.fillStyle = "rgba(255, 0, 0, 0.08)";
      ctx.fillRect(
        uncoveredStartPx,
        0,
        uncoveredWidthPx,
        feetToPixels(hangar.depth)
      );

      // Add diagonal hatching
      ctx.strokeStyle = "rgba(255, 0, 0, 0.3)";
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);

      for (let y = 0; y < feetToPixels(hangar.depth); y += 20) {
        ctx.beginPath();
        ctx.moveTo(uncoveredStartPx, y);
        ctx.lineTo(uncoveredStartPx + uncoveredWidthPx, y + uncoveredWidthPx);
        ctx.stroke();
      }

      // Label uncovered area (rotated for vertical text)
      ctx.setLineDash([]);
      ctx.save();
      ctx.translate(
        uncoveredStartPx + uncoveredWidthPx / 2,
        feetToPixels(hangar.depth / 2)
      );
      ctx.rotate(-Math.PI / 2);
      ctx.fillStyle = "#c62828";
      ctx.font = "bold 14px Lexend Deca, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(
        `UNCOVERED: ${(hangar.width - coveredWidth).toFixed(1)}ft`,
        0,
        0
      );
      ctx.restore();
    }
  }

  ctx.restore();
};

/**
 * Detect if bays are arranged longitudinally (front-to-back)
 * by checking if all bays share the same X-range (full width)
 */
function detectLongitudinalOrientation(bays: HangarBay[]): boolean {
  if (bays.length < 2) return false;

  const getXRange = (bay: HangarBay) => {
    const coords = bay.geom.coordinates[0];
    const xValues = coords.map((c) => c[0]);
    return { min: Math.min(...xValues), max: Math.max(...xValues) };
  };

  const firstRange = getXRange(bays[0]);

  // Check if all other bays have the same X-range (within tolerance)
  return bays.slice(1).every((bay) => {
    const range = getXRange(bay);
    return (
      Math.abs(range.min - firstRange.min) < 0.5 &&
      Math.abs(range.max - firstRange.max) < 0.5
    );
  });
}

/**
 * Draw horizontal divider lines for longitudinal bays
 */
function drawLongitudinalDividers(
  ctx: CanvasRenderingContext2D,
  bays: HangarBay[],
  hangar: { width: number; depth: number },
  feetToPixels: (ft: number) => number
) {
  // Find unique Y-coordinates that appear as shared edges
  const yCoordinates = new Set<number>();

  bays.forEach((bay) => {
    const coords = bay.geom.coordinates[0];
    const yValues = coords.map((c) => c[1]);
    yValues.forEach((y) => yCoordinates.add(y));
  });

  // Get min/max Y to identify exterior edges
  const allY = Array.from(yCoordinates);
  const minY = Math.min(...allY);
  const maxY = Math.max(...allY);

  // Draw horizontal lines for internal dividers only
  allY.forEach((y) => {
    // Skip exterior edges
    if (Math.abs(y - minY) < 0.1 || Math.abs(y - maxY) < 0.1) return;

    // Get X-range from first bay (all bays span full width)
    const coords = bays[0].geom.coordinates[0];
    const xValues = coords.map((c) => c[0]);
    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);

    // Transform Y for canvas (bottom-up rendering)
    const canvasY = hangar.depth - y;

    ctx.beginPath();
    ctx.moveTo(round(feetToPixels(minX)), round(feetToPixels(canvasY)));
    ctx.lineTo(round(feetToPixels(maxX)), round(feetToPixels(canvasY)));
    ctx.strokeStyle = "#FFFFFF";
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 4]);
    ctx.stroke();
  });

  ctx.setLineDash([]);
}

/**
 * Draw vertical divider lines for lateral bays
 */
function drawLateralDividers(
  ctx: CanvasRenderingContext2D,
  bays: HangarBay[],
  feetToPixels: (ft: number) => number
) {
  // Find unique X-coordinates that appear as shared edges
  const xCoordinates = new Set<number>();

  bays.forEach((bay) => {
    const coords = bay.geom.coordinates[0];
    const xValues = coords.map((c) => c[0]);
    xValues.forEach((x) => xCoordinates.add(x));
  });

  // Get min/max X to identify exterior edges
  const allX = Array.from(xCoordinates);
  const minX = Math.min(...allX);
  const maxX = Math.max(...allX);

  // Draw vertical lines for internal dividers only
  allX.forEach((x) => {
    // Skip exterior edges
    if (Math.abs(x - minX) < 0.1 || Math.abs(x - maxX) < 0.1) return;

    // Get Y-range from first bay (all bays span full depth)
    const coords = bays[0].geom.coordinates[0];
    const yValues = coords.map((c) => c[1]);
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);

    ctx.beginPath();
    ctx.moveTo(round(feetToPixels(x)), round(feetToPixels(minY)));
    ctx.lineTo(round(feetToPixels(x)), round(feetToPixels(maxY)));
    ctx.strokeStyle = "#FFFFFF";
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 4]);
    ctx.stroke();
  });

  ctx.setLineDash([]);
}

/**
 * Draw bay label with position adjusted for orientation
 */
function drawBayLabel(
  ctx: CanvasRenderingContext2D,
  bay: HangarBay,
  index: number,
  hangar: { width: number; depth: number },
  feetToPixels: (ft: number) => number,
  isLongitudinal: boolean,
  options?: { showLabels?: boolean; showHeights?: boolean }
) {
  const { showLabels = true, showHeights = true } = options || {};
  if (!showLabels && !showHeights) return;

  const coordinates = bay.geom.coordinates[0];
  if (!coordinates || coordinates.length === 0) return;

  // Calculate center position
  const uniqueCoords = coordinates.slice(0, -1); // Remove duplicate closing point
  const centerX =
    uniqueCoords.reduce((sum, coord) => sum + coord[0], 0) /
    uniqueCoords.length;

  // Position at 25% from top for all bays to avoid hangar name watermark
  const yValues = uniqueCoords.map((coord) => coord[1]);
  const minY = Math.min(...yValues);
  const maxY = Math.max(...yValues);
  const geometryCenterY = minY + (maxY - minY) * 0.25;

  // Transform Y for canvas rendering (bottom-up for longitudinal)
  const centerY = isLongitudinal
    ? hangar.depth - geometryCenterY
    : geometryCenterY;

  const centerPixelX = round(feetToPixels(centerX));
  const centerPixelY = round(feetToPixels(centerY));

  // Watermark style: grey text
  ctx.fillStyle = "#888888";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  if (showLabels && showHeights) {
    ctx.font = "14px Lexend Deca, sans-serif";
    ctx.fillText(
      `Bay ${index + 1} - ${bay.clear_height}ft clear`,
      centerPixelX,
      centerPixelY
    );
  } else if (showLabels) {
    ctx.font = "14px Lexend Deca, sans-serif";
    ctx.fillText(`Bay ${index + 1}`, centerPixelX, centerPixelY);
  } else if (showHeights) {
    ctx.font = "14px Lexend Deca, sans-serif";
    ctx.fillText(`${bay.clear_height}ft clear`, centerPixelX, centerPixelY);
  }
}
